import { alpha, Components, Theme } from '@mui/material';
import { grey, red } from '@mui/material/colors';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { ACCENT, ACCENT_HOVER } from './accent';
import {
  CARD_RADIUS,
  CONTROL_RADIUS,
  CONTROL_SIZE,
  ROW_HOVER_ALPHA,
  ROW_SELECTED_ALPHA,
  surfaces,
} from './designTokens';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    accent: true;
    plain: true;
    danger: true;
    quiet: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    card: true;
    page: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    accent: true;
  }
}

/** Control sizing for a button that keeps a MUI colour, so it lines up with
 *  the accent and plain variants beside it. */
export const controlSizing = {
  minWidth: 'auto',
  boxSizing: 'border-box' as const,
  height: `${CONTROL_SIZE}px`,
  // Units are explicit because this object is used both in theme
  // styleOverrides (raw CSS) and in `sx`, where a bare `gap: 6` would be read
  // as 6 spacing units — 48px.
  paddingInline: '16px',
  gap: '6px',
  borderRadius: CONTROL_RADIUS,
  textTransform: 'none' as const,
  boxShadow: 'none',
  whiteSpace: 'nowrap' as const,
};

/**
 * Button and Paper already carry `styleOverrides` in ColorModeContext, so their
 * variants are exported separately and attached alongside — spreading a second
 * definition of the same component key would replace those overrides, not merge
 * with them.
 */
export const buttonVariants = (dark: boolean) => [
  {
    props: { variant: 'accent' as const },
    // The MuiButton root override hardcodes a hover per variant and outranks a
    // plain colour, so every hover here restates its own.
    style: {
      ...controlSizing,
      backgroundColor: ACCENT,
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: ACCENT_HOVER,
        color: '#FFFFFF',
        boxShadow: 'none',
      },
      // The variant sets its own background, so a disabled button keeps the
      // accent fill unless this deadens it explicitly.
      '&.Mui-disabled': {
        backgroundColor: dark ? 'rgba(255,255,255,0.08)' : '#E7E7EE',
        color: dark ? 'rgba(255,255,255,0.55)' : '#5F5F6B',
      },
    },
  },
  {
    props: { variant: 'plain' as const },
    style: {
      ...controlSizing,
      backgroundColor: surfaces(dark).surface,
      color: dark ? '#D4D4D8' : '#353535',
      border: '1px solid',
      borderColor: dark ? 'rgba(255,255,255,0.18)' : '#D7D7DB',
      '&.Mui-disabled': {
        backgroundColor: dark ? 'rgba(255,255,255,0.08)' : '#E7E7EE',
        color: dark ? 'rgba(255,255,255,0.55)' : '#5F5F6B',
        borderColor: dark ? 'rgba(255,255,255,0.12)' : '#E6E7EB',
      },
      '&:hover': {
        backgroundColor: dark ? 'rgba(255,255,255,0.06)' : '#F6F7F9',
        color: dark ? '#D4D4D8' : '#353535',
        borderColor: dark ? 'rgba(255,255,255,0.18)' : '#D7D7DB',
      },
    },
  },
  {
    // The destructive action: what `variant="text" color="error"` rendered,
    // at control size.
    props: { variant: 'danger' as const },
    style: {
      ...controlSizing,
      backgroundColor: 'transparent',
      color: dark ? red[400] : red[500],
      '&:hover': {
        backgroundColor: 'rgba(0,0,0, 0)',
        color: dark ? red[200] : red[700],
      },
    },
  },
  {
    // Muted at rest, accent on hover — the footer's policy links.
    props: { variant: 'quiet' as const },
    style: {
      textTransform: 'none' as const,
      borderRadius: CONTROL_RADIUS,
      backgroundColor: 'transparent',
      color: dark ? grey[500] : grey[700],
      '&:hover': {
        color: ACCENT,
        backgroundColor: alpha(ACCENT, 0.06),
      },
    },
  },
];

export const paperVariants = (dark: boolean) => {
  const S = surfaces(dark);
  const flat = { backgroundImage: 'none', backdropFilter: 'none' };

  return [
    {
      props: { variant: 'card' as const },
      style: {
        ...flat,
        border: '1px solid',
        borderColor: S.line,
        borderRadius: CARD_RADIUS,
        backgroundColor: S.surface,
        boxShadow: S.cardShadow,
      },
    },
    {
      // The shell renders every page inside a padded Container, so a page's own
      // surface has to bleed back out to the edges.
      props: { variant: 'page' as const },
      style: {
        ...flat,
        boxShadow: 'none',
        borderRadius: 0,
        backgroundColor: S.pageBg,
        marginInline: '-24px',
        marginBlockStart: '-32px',
        paddingInline: '24px',
        paddingBlock: '16px',
        paddingBlockStart: '28px',
        minHeight: 'calc(100vh - 48px)',
        '@media (max-width:599.95px)': {
          marginInline: '-16px',
          paddingInline: '16px',
        },
      },
    },
  ];
};

/**
 * Everything else in the redesign that maps onto a MUI component. A page gets
 * these by rendering the component — a plain `<Select>`, `<Table>`, `<Menu>` —
 * with no per-page styling at all.
 */
export const buildComponents = (dark: boolean): Components<Theme> => {
  const S = surfaces(dark);

  // A portal escapes every parent selector, so menus and pickers can only be
  // reached from the theme. Without this they fall back to the translucent,
  // blurred Paper and are unreadable over content.
  const popupSurface = {
    backgroundColor: S.surface,
    backgroundImage: 'none',
    backdropFilter: 'none',
    borderRadius: CONTROL_RADIUS,
    boxShadow: S.cardShadow,
    border: '1px solid',
    borderColor: S.line,
    '& .MuiMenuItem-root:hover': {
      backgroundColor: alpha(ACCENT, ROW_HOVER_ALPHA),
    },
    '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: alpha(ACCENT, ROW_SELECTED_ALPHA),
    },
    '& .MuiPickersDay-root:hover': {
      backgroundColor: alpha(ACCENT, ROW_HOVER_ALPHA),
    },
    '& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus':
      {
        backgroundColor: ACCENT,
        color: '#FFFFFF',
      },
    '& .MuiPickersDay-today': { borderColor: ACCENT },
  };

  return {
    // IconButton has no variants API in v5, so the filled accent action is
    // keyed off a custom colour instead: `<IconButton color="accent">`.
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.color === 'accent'
            ? {
                width: CONTROL_SIZE,
                height: CONTROL_SIZE,
                backgroundColor: ACCENT,
                color: '#FFFFFF',
                '&:hover': { backgroundColor: ACCENT_HOVER },
                '&.Mui-disabled': {
                  backgroundColor: dark ? 'rgba(255,255,255,0.08)' : '#E7E7EE',
                  color: dark ? 'rgba(255,255,255,0.55)' : '#5F5F6B',
                },
              }
            : {},
      },
    },

    MuiOutlinedInput: {
      // The visible border is the fieldset, not the root. Only MuiTextField had
      // a rule for it, so a bare Select or Autocomplete kept MUI's 4px.
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: CONTROL_RADIUS,
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: CONTROL_RADIUS,
            borderColor: S.line,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(ACCENT, 0.5),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ACCENT,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-error:hover .MuiOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.error.main,
            },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.disabled,
          },
        }),
      },
    },

    MuiMenu: { styleOverrides: { paper: popupSurface } },
    MuiPopover: { styleOverrides: { paper: popupSurface } },
    // The picker's surface is a Paper, so MuiPaper's own root override lands on
    // the same element. `&&` doubles specificity so this wins regardless of the
    // order the two stylesheets are injected in.
    MuiPickersPopper: { styleOverrides: { paper: { '&&': popupSurface } } },

    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          background: S.headerBg,
          '& .MuiTableCell-root': {
            height: 38,
            paddingBlock: 0,
            borderBottomColor: S.line,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: dark ? '#A1A1AA' : '#52525B',
          },
          // A stickyHeader table paints the head background onto the cells, so
          // the row background above would sit invisibly behind them.
          '& .MuiTableCell-stickyHeader': { backgroundColor: S.headerBg },
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        // Zebra striping is not a MUI concept, so a table opts in with
        // `data-zebra`. The hover and selected rules are re-stated under the
        // same attribute at higher specificity — otherwise the stripe wins and
        // a striped row never looks hovered or selected.
        root: {
          '&[data-zebra] .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)':
            {
              backgroundColor: S.pageBg,
            },
          '&[data-zebra] .MuiTableBody-root .MuiTableRow-root.MuiTableRow-hover:hover':
            {
              backgroundColor: alpha(ACCENT, ROW_HOVER_ALPHA),
            },
          '&[data-zebra] .MuiTableBody-root .MuiTableRow-root.Mui-selected, &[data-zebra] .MuiTableBody-root .MuiTableRow-root.Mui-selected:hover':
            {
              backgroundColor: alpha(ACCENT, ROW_SELECTED_ALPHA),
            },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(ACCENT, ROW_HOVER_ALPHA),
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(ACCENT, ROW_SELECTED_ALPHA),
          },
        },
      },
    },
  };
};
